import { useEffect, useRef } from 'react'
import {
  Relay,
  RELAY_SIGNALS,
  createShortPollingStrategy,
  type RelayEnvironment,
  type RelayStrategy,
} from '@heard/utils/relay'
import { logHeardError } from '@/utils/errorHelpers'

interface HavenRelayRefreshers {
  refreshConversations: () => Promise<void>
  refreshActiveConversation: () => Promise<void>
}

const POLL_INTERVAL_MS = 5_000
const MAX_POLL_INTERVAL_MS = 60_000
const ALL_CHANGED_EVERY_TICKS = 3

export const createHavenRelay = (
  strategy: RelayStrategy,
  refreshers: HavenRelayRefreshers,
  environment?: RelayEnvironment
): Relay => {
  const relay = new Relay(strategy, environment, (error) => {
    logHeardError(error, { component: 'Haven Relay' })
  })
  relay.subscribe(
    RELAY_SIGNALS.conversationChanged,
    refreshers.refreshConversations
  )
  relay.subscribe(
    RELAY_SIGNALS.conversationChanged,
    refreshers.refreshActiveConversation
  )
  relay.subscribe(RELAY_SIGNALS.casesChanged, refreshers.refreshConversations)
  relay.subscribe(
    RELAY_SIGNALS.casesChanged,
    refreshers.refreshActiveConversation
  )
  relay.subscribe(
    RELAY_SIGNALS.activeConversationChanged,
    refreshers.refreshActiveConversation
  )
  return relay
}

export const useHavenRelay = (
  enabled: boolean,
  viewerId: number | undefined,
  openHumanConversation: boolean,
  refreshers: HavenRelayRefreshers
): void => {
  const refreshersRef = useRef(refreshers)
  refreshersRef.current = refreshers
  const openHumanConversationRef = useRef(openHumanConversation)
  openHumanConversationRef.current = openHumanConversation

  useEffect(() => {
    const relay = createHavenRelay(
      createShortPollingStrategy({
        intervalMs: POLL_INTERVAL_MS,
        maxIntervalMs: MAX_POLL_INTERVAL_MS,
        cadence: {
          allChangedEveryTicks: ALL_CHANGED_EVERY_TICKS,
          intermediateSignal: RELAY_SIGNALS.activeConversationChanged,
          shouldPublishIntermediate: () => openHumanConversationRef.current,
        },
      }),
      {
        refreshConversations: () =>
          refreshersRef.current.refreshConversations(),
        refreshActiveConversation: () =>
          refreshersRef.current.refreshActiveConversation(),
      }
    )
    relay.setEnabled(enabled)
    return () => relay.dispose()
  }, [enabled, viewerId])
}
